import React, { useEffect, useState } from "react"
import api from "../services/api"
import Skeleton from "./skeleton/Skeleton"
import { Search } from "lucide-react"
import { Helmet } from "react-helmet"
import { Trash, EditIcon } from "lucide-react"
import Swal from "sweetalert2"

export default function Tenants() {
  const [errors, setErrors] = useState({})
  const [tenants, setTenants] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [tenantToDelete, setTenantToDelete] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isServerOnline, setIsServerOnline] = useState(true)
  const [errorMsg, setErrorMsg] = useState("")
  const [filter, setFilter] = useState("")
  const [idFile, setIdFile] = useState(null)
  const [isScanningId, setIsScanningId] = useState(false)

  const phoneRegex = /^09\d{9}$/

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    contact: "",
    email: "",
    moveInDate: "",
    contractEnd: "",
    emergencyContact: "",
  })

  const emptyForm = {
    name: "",
    address: "",
    contact: "",
    email: "",
    moveInDate: "",
    contractEnd: "",
    emergencyContact: "",
  }

  const handleIdUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIdFile(file)
    setIsScanningId(true)

    const uploadData = new FormData()
    uploadData.append("idImage", file)

    try {
      const res = await api.post("/api/ocr/id", uploadData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      const extracted = res.data

      setFormData((prev) => ({
        ...prev,
        name: extracted.name || prev.name,
        address: extracted.address || prev.address,
        contact: extracted.contact || prev.contact,
        email: extracted.email || prev.email,
      }))

      Swal.fire({
        icon: "success",
        text: "ID scanned successfully.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error("Failed to scan ID", err)

      Swal.fire({
        icon: "error",
        title: "Scan failed",
        text: "Could not read the ID clearly. Please enter the details manually.",
        confirmButtonColor: "#f97316",
      })
    } finally {
      setIsScanningId(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Name
    if (!formData.name.trim()) {
      newErrors.name = "Tenant name is required"
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required"
    }

    // Contact
    if (!formData.contact.trim()) {
      newErrors.contact = "Contact number is required"
    } else if (!phoneRegex.test(formData.contact)) {
      newErrors.contact = "Contact number must be 11 digits (09xxxxxxxxx)"
    }

    // Email
    if (!formData.email) {
      newErrors.email = "Email is required"
    }

    // Emergency contact
    if (!formData.emergencyContact.trim()) {
      newErrors.emergencyContact = "Emergency contact is required"
    } else if (!phoneRegex.test(formData.emergencyContact)) {
      newErrors.emergencyContact =
        "Contact number must be 11 digits (09xxxxxxxxx)"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // FETCH TENANTS
  const fetchTenants = async () => {
    setLoading(true)
    setErrorMsg("")

    try {
      const res = await api.get(`/api/tenants`)

      setTenants(res.data)
      setIsServerOnline(true)
    } catch (err) {
      setIsServerOnline(false)
      setTenants([])
      setErrorMsg("The server is currently offline. Please refresh the page.")
      console.error("Failed to fetch tenants", err.response || err)
    } finally {
      setLoading(false)
    }
  }

  // Filter tenants based on the filter text
  const filteredTenants = tenants.filter((tenant) => {
    const lowercasedFilter = filter.toLowerCase()
    return (
      tenant.name.toLowerCase().includes(lowercasedFilter) ||
      tenant.contact.includes(filter) ||
      (tenant.email && tenant.email.toLowerCase().includes(lowercasedFilter))
    )
  })

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    setErrors((prev) => {
      if (!prev[name]) return prev
      const { [name]: _, ...rest } = prev
      return rest
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      await api.post(`/api/tenants`, formData)

      Swal.fire({
        icon: "success",
        text: "Add successfully.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })

      setIsAddModalOpen(false)
      setFormData(emptyForm)
      setErrors({})

      fetchTenants()
    } catch (err) {
      // axios network error (server offline)
      if (api.isAxiosError(err) && !err.response) {
        Swal.fire({
          icon: "error",
          title: "Backend is offline",
          text: "Cannot connect to http://localhost:5000. Start the server and try again.",
        })
        return
      }

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          err.response?.data?.message ||
          "Something went wrong while saving changes.",
      })
    }
  }

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant)
    setErrors({})

    setFormData({
      name: tenant.name || "",
      address: tenant.address || "",
      contact: tenant.contact || "",
      email: tenant.email || "",
      moveInDate: tenant.moveInDate ? tenant.moveInDate.slice(0, 10) : "",
      contractEnd: tenant.contractEnd ? tenant.contractEnd.slice(0, 10) : "",
      emergencyContact: tenant.emergencyContact || "",
    })

    setIsModalOpen(true)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      const res = await api.put(`/api/tenants/${selectedTenant._id}`, formData)

      setIsModalOpen(false)

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Tenant details have been saved.",
        confirmButtonColor: "#f97316",
        timer: 1500,
        showConfirmButton: false,
      })

      setTenants((prev) =>
        prev.map((t) => (t._id === selectedTenant._id ? res.data : t))
      )

      setIsModalOpen(false)
      setSelectedTenant(null)
      fetchTenants()
      setErrors({})
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: "Something went wrong while saving changes.",
      })
    }
  }

  const confirmDelete = async () => {
    if (!tenantToDelete) return

    try {
      await api.delete(`/api/tenants/${tenantToDelete._id}`)

      setTenants((prev) => prev.filter((t) => t._id !== tenantToDelete._id))

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        confirmButtonColor: "#d1001f",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error(err)
      alert("Failed to delete tenant")
    } finally {
      setIsDeleteModalOpen(false)
      setTenantToDelete(null)
    }
  }

  useEffect(() => {
    fetchTenants()
  }, [])

  useEffect(() => {
    if (Object.keys(errors).length === 0) return

    const timer = setTimeout(() => {
      setErrors({})
    }, 5000)

    return () => clearTimeout(timer)
  }, [errors])

  const formatDate = (dateString) => {
    if (!dateString) return "—"

    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })
  }

  return (
    <>
      <Helmet>
        <title>Tenants - Stay Suite | Apartment Management System</title>
      </Helmet>
      <div className="p-6 w-full max-w-300 mx-auto">
        {loading ? (
          <Skeleton />
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-2">Tenants</h1>
            <p className="pb-5 text-gray-600 italic text-sm">
              Fill in the details below to manage tenant information.
            </p>
            <div className="flex justify-between flex-col md:flex-row mb-6 md:mb-2">
              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search available rooms..."
                  className="h-10 w-full md:w-64 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              <button
                onClick={() => {
                  setFormData(emptyForm)
                  setErrors({})
                  setIsAddModalOpen(true)
                }}
                className={`h-10 rounded-lg px-5 text-sm font-semibold text-white shadow-sm
    ${isServerOnline ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-400 cursor-not-allowed"}`}
              >
                + Add New Tenant
              </button>
            </div>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-160 w-full border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Contact #
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Email
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Moved Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Contract End
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {filteredTenants.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-10 text-center text-gray-500 italic"
                        >
                          No data found
                        </td>
                      </tr>
                    ) : (
                      filteredTenants.map((tenant, index) => (
                        <tr
                          key={tenant._id}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3">{tenant.name}</td>
                          <td className="px-4 py-3">{tenant.contact}</td>
                          <td className="px-4 py-3">{tenant.email || "—"}</td>
                          <td className="px-4 py-3">
                            {formatDate(tenant.moveInDate)}
                          </td>
                          <td className="px-4 py-3">
                            {formatDate(tenant.contractEnd)}
                          </td>
                          <td className="px-4 py-3 flex gap-2">
                            <button
                              onClick={() => openEditModal(tenant)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                            >
                              <EditIcon size={18} />
                            </button>
                            <button
                              onClick={() => {
                                setTenantToDelete(tenant)
                                setIsDeleteModalOpen(true)
                              }}
                              className="p-2 rounded-lg  text-red-500 hover:bg-red-200 transition"
                            >
                              <Trash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {!isServerOnline && (
              <div className="mt-4 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {errorMsg}
                <button className="ml-3 underline" onClick={fetchTenants}>
                  Retry
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Assign Tenant
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Fill in tenant details below
            </p>

            <form
              className="space-y-4"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              {/* Name */}
              <div className="space-y-1">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Tenant Name
                </label>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />
                {/* 
                <div className="space-y-1 rounded-lg border border-dashed border-orange-300 bg-orange-50 p-4">
                  <label className="text-sm font-medium text-gray-700">
                    Upload Valid ID
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIdUpload}
                    className="w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-orange-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-700"
                  />

                  <p className="text-xs text-gray-500">
                    Upload a clear photo of the tenant ID to auto-fill the form.
                  </p>

                  {isScanningId && (
                    <p className="text-xs font-medium text-orange-600">
                      Scanning ID, please wait...
                    </p>
                  )}

                  {idFile && !isScanningId && (
                    <p className="text-xs text-gray-500">
                      Selected: {idFile.name}
                    </p>
                  )}
                </div> */}

                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}

                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Address
                </label>
                <input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Your Address"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />

                {errors.address && (
                  <p className="text-xs text-red-500">{errors.address}</p>
                )}
              </div>

              {/* Contact */}
              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Contact Number
                </label>
                <input
                  type="tel"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="09xxxxxxxxx"
                  maxLength={11}
                  pattern="[0-9]{11}"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />

                {errors.contact && (
                  <p className="text-xs text-red-500">{errors.contact}</p>
                )}
              </div>

              {/* Emergency Contact */}
              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Emergency Contact
                </label>
                <input
                  type="tel"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  maxLength={11}
                  pattern="[0-9]{11}"
                  placeholder="Emergency contact number"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />
                {errors.emergencyContact && (
                  <p className="text-xs text-red-500">
                    {errors.emergencyContact}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />

                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Move In */}
              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Move In Date
                </label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />
              </div>

              {/* Contract End */}
              <div className="space-y-0.5">
                <label className="text-sm font-medium text-gray-700">
                  Contract End Date
                </label>
                <input
                  type="date"
                  name="contractEnd"
                  value={formData.contractEnd}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                       transition"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false)
                    setFormData(emptyForm)
                    setErrors({})
                  }}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-300
                       text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold
                       bg-orange-600 text-white hover:bg-orange-700
                       focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
                       transition"
                >
                  Add Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Delete Tenant
            </h2>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete tenant{" "}
              <span className="font-semibold">{tenantToDelete?.name}</span>?
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setTenantToDelete(null)
                }}
                className="px-4 py-2 rounded-lg border text-sm
                     text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-sm font-semibold
                     bg-red-600 text-white hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Edit Tenant
            </h2>
            <p className="text-sm text-gray-500 mb-5">Update tenant details</p>

            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Name */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500 ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500 ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
                />
                {errors.address && (
                  <p className="text-xs text-red-500">{errors.address}</p>
                )}
              </div>

              {/* Contact */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Contact
                </label>
                <input
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  maxLength={11}
                  pattern="[0-9]{11}"
                  className={`w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500 ${
                errors.contact ? "border-red-500" : "border-gray-300"
              }`}
                />
                {errors.contact && (
                  <p className="text-xs text-red-500">{errors.contact}</p>
                )}
              </div>

              {/* Emergency Contact */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Emergency Contact
                </label>
                <input
                  type="tel"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  maxLength={11}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500 ${
                errors.emergencyContact ? "border-red-500" : "border-gray-300"
              }`}
                />
                {errors.emergencyContact && (
                  <p className="text-xs text-red-500">
                    {errors.emergencyContact}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500 ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Move In Date */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Move In Date
                </label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500 ${
                errors.moveInDate ? "border-red-500" : "border-gray-300"
              }`}
                />
                {errors.moveInDate && (
                  <p className="text-xs text-red-500">{errors.moveInDate}</p>
                )}
              </div>

              {/* Contract End */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Contract End
                </label>
                <input
                  type="date"
                  name="contractEnd"
                  value={formData.contractEnd}
                  onChange={handleChange}
                  className="w-full rounded-lg border px-3 py-2 text-sm
              focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false)
                    setSelectedTenant(null)
                    setFormData(emptyForm)
                    setErrors({})
                  }}
                  className="px-4 py-2 rounded-lg border text-sm
                       text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold
                       bg-orange-600 text-white hover:bg-orange-700 transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
